import React, { useEffect, useState } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import SideBar from "./SideBar";
import ReactPaginate from "react-paginate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarCheck } from "@fortawesome/free-solid-svg-icons";

const API_URL = process.env.REACT_APP_API_URL;

const LeaveRequestsTable = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveCounts, setLeaveCounts] = useState({
    sickLeave: 0,
    paidLeave: 0,
    restrictedHoliday: 0,
    menstrualLeave: 0,
    regularizationLeave: 0,
    compensationLeave: 0,
    onOfficeDuty: 0,
  });

  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const pageCount = Math.ceil(leaveRequests.length / itemsPerPage);
  const displayedLeaveRequests = leaveRequests.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );

  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        const token = await localforage.getItem("token");
        const response = await api.get(`${API_URL}/leavedata/leave-requests`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setLeaveRequests(response.data.leaveRequests);
      } catch (err) {
        console.error("Error fetching leave requests:", err);
        setError("Failed to fetch leave requests. Please try again later.");
      }
    };

    const fetchLeaveCounts = async () => {
      try {
        const token = await localforage.getItem("token");
        const email = await localforage.getItem("email");
        const response = await api.get(
          `${API_URL}/employeedata/get-leave-data/${email}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setLeaveCounts(response.data.leaveData);
      } catch (err) {
        console.error("Error fetching leave counts:", err);
        setError("Failed to fetch leave counts. Please try again later.");
      }
    };

    fetchLeaveRequests();
    fetchLeaveCounts();
  }, []);

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  const formatLeaveType = (type) =>
    type.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const LeaveCountCard = ({ title, count, icon, description }) => (
    <div className="stat-card">
      <div className="stat-card-header">
        <span className="stat-card-title">{title}</span>
        <div className="stat-card-icon">
          <FontAwesomeIcon icon={icon || faCalendarCheck} />
        </div>
      </div>
      <div className="stat-card-value">{count}</div>
      <div className="stat-card-description">{description}</div>
    </div>
  );

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">My Leave Requests</h1>
          <p className="dashboard-subtitle">
            Track and manage your leave applications
          </p>
        </div>

        {/* Leave Balance Cards */}
        <div className="stats-grid">
          <LeaveCountCard
            title="Sick Leave"
            count={leaveCounts.sickLeave}
            description="Leaves Available"
          />
          <LeaveCountCard
            title="Paid Leave"
            count={leaveCounts.paidLeave}
            description="Leaves Available"
          />
          <LeaveCountCard
            title="Restricted Holiday"
            count={leaveCounts.restrictedHoliday}
            description="Leaves Available"
          />
          <LeaveCountCard
            title="Menstrual Leave"
            count={leaveCounts.menstrualLeave}
            description="Leaves Available"
          />
          <LeaveCountCard
            title="Leave Regularization"
            count={leaveCounts.regularizationLeave}
            description="Leaves Available"
          />
          <LeaveCountCard
            title="On Office Duty"
            count={leaveCounts.onOfficeDuty}
            description="Days Taken"
          />
          <LeaveCountCard
            title="Compensation Leave"
            count={leaveCounts.compensationLeave}
            description="Days Taken"
          />
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {leaveRequests.length > 0 ? (
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">Leave Request History</h3>
            </div>
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Reporting Manager</th>
                    <th>Leave Type</th>
                    <th>Reason</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Summary</th>
                    <th>Leave Code</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedLeaveRequests.map((leave) => (
                    <tr key={leave._id}>
                      <td>{leave.receiverEmail}</td>
                      <td>{formatLeaveType(leave.leaveType)}</td>
                      <td>{leave.reasonForLeave}</td>
                      <td>
                        {new Date(leave.startDate).toLocaleDateString("en-GB")}
                      </td>
                      <td>
                        {new Date(leave.endDate).toLocaleDateString("en-GB")}
                      </td>
                      <td style={{ minWidth: "140px" }}>
                        {leave.summaryForLeave}
                      </td>
                      <td>
                        <strong>{leave.leaveCode}</strong>
                      </td>
                      <td>
                        <span
                          className={
                            leave.leaveStatus === "approved"
                              ? "status-badge status-badge-success"
                              : leave.leaveStatus === "not approved"
                                ? "status-badge status-badge-rejected"
                                : "status-badge status-badge-pending"
                          }
                        >
                          {capitalizeWords(leave.leaveStatus)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div className="pagination">
                <ReactPaginate
                  previousLabel={"← Previous"}
                  nextLabel={"Next →"}
                  breakLabel={"..."}
                  pageCount={pageCount}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  containerClassName={"pagination"}
                  pageClassName={"pagination-btn"}
                  pageLinkClassName={"pagination-link"}
                  activeClassName={"active"}
                  previousClassName={"pagination-btn"}
                  nextClassName={"pagination-btn"}
                  disabledClassName={"disabled"}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="card text-center p-4">
            <p>No leave requests found.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default LeaveRequestsTable;
